import { Badge } from "../../../../components/ui/common/badge";
import { TableCell, TableRow } from "../../../../components/ui/common/table";
import { getCompanyName } from "../../../../utils/cacheStorage";
import UserAvatar from "../../shared/components/UserAvatar";
import { UserRow } from "../../shared/types/user.types";
import UserRowActions from "./UserRowActions";
import { userTableColumnClass } from "./userTable.columns";

interface UserTableRowProps {
  row: UserRow;
  onView: (row: UserRow) => void;
  onEdit: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
}

/** "DD/MM/YYYY" (UTC); "—" for a missing/invalid date instead of throwing. */
const formatCreatedAt = (value?: string) => {
  const date = value ? new Date(value) : null;
  if (!date || isNaN(date.getTime())) return "—";
  return date.toISOString().slice(0, 10).split("-").reverse().join("/");
};

const formatStatus = (status: string) => status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

const getInitials = (first?: string, last?: string) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "U";

/** One user's grid row. */
const UserTableRow = ({ row, onView, onEdit, onDelete }: UserTableRowProps) => {
  const fullName = [row.firstName, row.lastName].filter(Boolean).join(" ") || "—";
  const role = row.userRole?.[0];
  // Users belong to the signed-in user's tenant; fall back to it when the row carries no company name.
  const company = row.companyName || row.comapanyName || getCompanyName() || "—";

  return (
    <TableRow className="tw-bg-white hover:tw-bg-muted/50">
      <TableCell className={userTableColumnClass.name}>
        <div className="tw-flex tw-items-center tw-gap-3">
          <UserAvatar
            src={row.profileUrl}
            alt={fullName}
            initials={getInitials(row.firstName, row.lastName)}
            className="tw-h-9 tw-w-9"
          />
          <div className="tw-min-w-0">
            <div className="tw-text-sm tw-font-medium tw-text-foreground">{fullName}</div>
            <div className="tw-mt-0.5 tw-break-all tw-text-xs tw-text-muted-foreground lg:tw-hidden">
              {row.email || "—"}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className={`${userTableColumnClass.email} tw-break-all tw-text-sm tw-text-foreground`}>
        {row.email || "—"}
      </TableCell>
      <TableCell className={`${userTableColumnClass.company} tw-text-sm tw-text-foreground`}>{company}</TableCell>
      <TableCell className={userTableColumnClass.role}>
        {role ? (
          <Badge variant="outline" className="tw-rounded-full tw-text-[11px] tw-font-medium">
            {role}
          </Badge>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell className={`${userTableColumnClass.status} tw-text-sm tw-text-foreground`}>
        {row.status ? formatStatus(row.status) : "—"}
      </TableCell>
      <TableCell className={`${userTableColumnClass.created} tw-font-mono tw-text-xs tw-text-foreground`}>
        {formatCreatedAt(row.createdAt)}
      </TableCell>
      <TableCell className={userTableColumnClass.actions}>
        <UserRowActions row={row} onView={onView} onEdit={onEdit} onDelete={onDelete} />
      </TableCell>
    </TableRow>
  );
};

export default UserTableRow;
